import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import { BasePreviewShellComponent } from '../../google-templates/preview-common/base-preview-shell.component';
import { PreviewVariant } from '../../google-templates/preview-common/preview-variant.interface';
import { MetaPreviewData } from '../../../../../models/meta-template/preview-interfaces/meta-preview-data.interface';
import { META_VIDEO_VARIANTS } from './variants/meta-video-variants';

/**
 * Meta Video Ad preview. The selector mechanics live in
 * {@link BasePreviewShellComponent}; this class supplies the variant registry
 * and how to build the flat {@link MetaPreviewData} DTO from the blocks.
 *
 * To add a placement, register it in `META_VIDEO_VARIANTS`.
 */
@Component({
  selector: 'app-meta-video-preview',
  imports: [CommonModule, NgComponentOutlet],
  templateUrl: '../../google-templates/preview-common/base-preview-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../google-templates/preview-common/base-preview-shell.component.scss'
})
export class MetaVideoPreviewComponent extends BasePreviewShellComponent<MetaPreviewData> {
  protected readonly registry: PreviewVariant[] = META_VIDEO_VARIANTS;

  /**
   * Open on the registry's first entry. The base shell starts on desktop, and
   * Facebook has desktop placements too, so it would otherwise open on those.
   */
  override ngOnInit(): void {
    const [first] = this.registry;
    if (first) {
      this.platform = first.platform;
      this.device = first.device;
    }
    super.ngOnInit();
  }

  protected buildPreviewData(): MetaPreviewData {
    return {
      headlines: PreviewBlockHelper.collectFromBlock(this.blocks, 'meta-video-headline', 'MetaVideoHeadline_', 5),
      primaryTexts: PreviewBlockHelper.collectFromBlock(this.blocks, 'meta-video-primary-text', 'MetaVideoPrimaryText_', 5),
      creative: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'meta-video-video'), 'Video').trim(),
      creativeKind: 'video',
      ctaText: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'meta-video-cta-text'), 'Text').trim(),
      url: this.toAbsoluteUrl(PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'meta-video-url'), 'Link')),
      accountType: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'meta-video-account-type'), 'AccountType').trim(),
    };
  }

  /** Prefix a scheme so an `[href]` does not resolve against the editor's origin. */
  private toAbsoluteUrl(rawUrl: string): string {
    const url = rawUrl.trim();
    if (!url) {
      return '';
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
